/**
 * WeaveIn.ai Platform — generic member outreach tracking API client.
 *
 * Billing-agnostic counterpart to outreachBilling.ts: every org can log outreach
 * touches on a member (SMS / email / phone) and record in-person visits captured
 * via an intake/assessment form, and read the unified touch trail. The billing
 * view (claims / $) is separate and gated by the `outreach_billing` capability.
 *
 * Backend: /api/member-outreach/{member_id}/... (see routers/member_outreach.py).
 */

import axios, { AxiosInstance } from 'axios';

import { config as productConfig } from '@/src/config';

export type OutreachChannel = 'sms' | 'email' | 'phone' | 'in_person';

export interface MemberOutreachTouch {
  id: string; // 'c<id>' for a communication touch, 'a<id>' for an in-person activity
  channel: OutreachChannel;
  direction: string; // 'outbound' | 'inbound' | 'in_person'
  minutes: number | null;
  engagement_type: string | null;
  notes: string;
  form_instance_id: number | null;
  form_name?: string;
  location: string | null;
  date: string | null;
  source: 'communication' | 'in_person';
}

export interface MemberOutreachTouches {
  billing_enabled: boolean;
  counts: { total: number; sms: number; email: number; phone: number; in_person: number };
  touches: MemberOutreachTouch[];
}

export interface InPersonFormOption {
  id: number;
  name: string;
  form_type: string;
  description?: string | null;
}

export interface OutreachTemplate {
  id: number;
  name: string;
  subject?: string | null;
  body?: string | null;
  communication_method?: string | null;
}

export interface OutreachTypeConfig {
  engagement_types: Array<{ value: string; label: string }>;
  disposition_types: Array<{ value: string; label: string }>;
}

class MemberOutreachApiService {
  private client: AxiosInstance;

  constructor(baseURL: string) {
    this.client = axios.create({ baseURL, headers: { 'Content-Type': 'application/json' } });
    this.client.interceptors.request.use((cfg) => {
      const token = localStorage.getItem('auth_token');
      const cboId = localStorage.getItem('active_cbo_id');
      if (token) cfg.headers.Authorization = `Bearer ${token}`;
      if (cboId) cfg.headers['X-CBO-ID'] = cboId;
      return cfg;
    });
  }

  async getTouches(memberId: number): Promise<MemberOutreachTouches> {
    const { data } = await this.client.get(`/member-outreach/${memberId}/touches`);
    return data;
  }

  async logTouch(
    memberId: number,
    payload: {
      channel: 'sms' | 'email' | 'phone';
      minutes?: number;
      direction?: 'outbound' | 'inbound';
      engagement_type?: string;
      notes?: string;
      date?: string;
    },
  ): Promise<MemberOutreachTouch> {
    const { data } = await this.client.post(`/member-outreach/${memberId}/touch`, payload);
    return data;
  }

  async getInPersonForms(memberId: number): Promise<InPersonFormOption[]> {
    const { data } = await this.client.get(`/member-outreach/${memberId}/in-person-forms`);
    return data.forms ?? [];
  }

  async recordInPersonTouch(
    memberId: number,
    payload: {
      form_instance_id: number;
      minutes?: number;
      location?: string;
      notes?: string;
      engagement_type?: string;
    },
  ): Promise<MemberOutreachTouch> {
    const { data } = await this.client.post(`/member-outreach/${memberId}/in-person-touch`, payload);
    return data;
  }

  // --- touch parity with billing orgs: templates, engagement types, real sends ---

  async getTemplates(channel: 'sms' | 'email' | 'phone'): Promise<OutreachTemplate[]> {
    const { data } = await this.client.get(`/member-outreach/templates`, { params: { channel } });
    return data.templates ?? [];
  }

  async getTypeConfig(memberId: number): Promise<OutreachTypeConfig> {
    const { data } = await this.client.get(`/member-outreach/${memberId}/type-config`);
    return data;
  }

  async sendSms(
    memberId: number,
    payload: { message?: string; template_id?: number; minutes?: number; to_number?: string; engagement_type?: string },
  ): Promise<{ sent: boolean; error?: string | null }> {
    const { data } = await this.client.post(`/member-outreach/${memberId}/send-sms`, payload);
    return data;
  }

  async sendEmail(
    memberId: number,
    payload: {
      subject?: string;
      message?: string;
      template_id?: number;
      minutes?: number;
      to_email?: string;
      engagement_type?: string;
    },
  ): Promise<{ sent: boolean; error?: string | null }> {
    const { data } = await this.client.post(`/member-outreach/${memberId}/send-email`, payload);
    return data;
  }

  async logCall(
    memberId: number,
    payload: { minutes?: number; notes?: string; to_number?: string; initiate?: boolean; engagement_type?: string },
  ): Promise<{ touch_id: number; dialed: boolean; error?: string | null }> {
    const { data } = await this.client.post(`/member-outreach/${memberId}/log-call`, payload);
    return data;
  }
}

const getApiBaseUrl = (): string =>
  (productConfig.weaveinApi.baseUrl ?? 'https://weavein-api.onrender.com').trim() + '/api';

export const memberOutreachApi = new MemberOutreachApiService(getApiBaseUrl());
